import { ImageIcon } from '@phosphor-icons/react';
import { UIImageCategory, UI_IMAGE_CATEGORIES, AnyCropData } from '@/modules/uiImages/types';
import { shownUrl } from '@/utils/localCopies';

interface FlexibleImageDisplayProps {
  imageUrl?: string;
  imageCroppedUrl?: string;
  crop?: AnyCropData; // Support both legacy and standard crop formats
  imageCategory: UIImageCategory;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  showCrop?: boolean;
  fallbackIcon?: React.ReactNode;
}

export function FlexibleImageDisplay({
  imageUrl: storedUrl,
  imageCroppedUrl: storedCroppedUrl,
  crop,
  imageCategory,
  size = 'md',
  className = '',
  showCrop = true,
  fallbackIcon
}: FlexibleImageDisplayProps) {
  const categoryConfig = UI_IMAGE_CATEGORIES[imageCategory];
  // The creator's own new upload shows from the file already in the browser
  const imageUrl = shownUrl(storedUrl);
  const imageCroppedUrl = shownUrl(storedCroppedUrl);

  const sizeClasses = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
    xl: 'h-48 w-48',
    full: 'w-full h-full'
  };

  const getAspectRatioClass = () => {
    switch (categoryConfig.aspectRatio) {
      case '9:16':
        return 'aspect-[9/16]';
      case '16:9':
        return 'aspect-video';
      case '1:1':
        return 'aspect-square';
      default:
        return 'aspect-video';
    }
  };

  const getDisplayClasses = () => {
    const baseClasses = sizeClasses[size];
    
    // For scan images and avatars, make them perfectly circular
    if (imageCategory === 'ScanImage' || imageCategory === 'Avatar') {
      return `${baseClasses} ${className} rounded-full overflow-hidden relative`;
    }
    
    // For other images, use aspect ratio classes
    const aspectClass = getAspectRatioClass();
    return `${baseClasses} ${className} ${aspectClass} overflow-hidden rounded-lureo-radius-lg relative`;
  };

  const getFallbackIcon = () => {
    if (fallbackIcon) return fallbackIcon;
    return <ImageIcon className="h-6 w-6 text-lureo-ink-subtlest" />;
  };

  // Determine which image URL to use
  const displayImageUrl = imageCroppedUrl || imageUrl;

  // If no image, show fallback
  if (!displayImageUrl) {
    return (
      <div
        className={`${getDisplayClasses()} bg-lureo-neutral flex items-center justify-center`}
      >
        {getFallbackIcon()}
      </div>
    );
  }

  // If we have a cropped URL, show it without any transforms
  if (imageCroppedUrl) {
    return (
      <div
        className={getDisplayClasses()}
      >
        <img
          src={imageCroppedUrl}
          alt={categoryConfig.description}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // If no crop data or crop is disabled, show original image normally
  if (!showCrop || !crop) {
    return (
      <div
        className={getDisplayClasses()}
      >
        <img
          src={imageUrl}
          alt={categoryConfig.description}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Use cropped URL if available, otherwise use original URL
  const finalImageUrl = imageCroppedUrl || imageUrl;
  
  if (!finalImageUrl) {
    // Show fallback icon if no image URL
    return (
      <div
        className={getDisplayClasses()}
      >
        {fallbackIcon || <ImageIcon className="h-8 w-8 text-lureo-ink-subtlest" />}
      </div>
    );
  }

  return (
    <div
      className={getDisplayClasses()}
    >
      <img
        src={finalImageUrl}
        alt={categoryConfig.description}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

// Export for use in other components
export default FlexibleImageDisplay;
